import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/adjuster`;
const TOKEN_KEY = "ag_portal_token";

export const portalToken = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

const client = axios.create({ baseURL: BASE });
client.interceptors.request.use((cfg) => {
  const t = portalToken.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      portalToken.clear();
      const p = window.location.pathname;
      if (!p.startsWith("/digital-card/login") && !p.startsWith("/digital-card/invite/")) {
        window.location.href = "/digital-card/login";
      }
    }
    return Promise.reject(err);
  }
);

export const portalApi = {
  // auth
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  // adjuster
  updateProfile: (p) => client.put("/profile", p).then((r) => r.data),
  uploadPhoto: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return client.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  publish: () => client.post("/profile/publish").then((r) => r.data),
  // public
  publicCard: (slug) => axios.get(`${BASE}/card/${slug}`).then((r) => r.data),
  adminPreviewCard: (slug) => client.get(`/admin/preview/${slug}`).then((r) => r.data),
  vcardUrl: (slug) => `${BASE}/card/${slug}/vcard`,
  trackEvent: (slug, event) => axios.post(`${BASE}/card/${slug}/event`, { event }).catch(() => {}),
  // invitations (public)
  getInvitation: (token) => axios.get(`${BASE}/invitations/${token}`).then((r) => r.data),
  acceptInvitation: (token, password) => axios.post(`${BASE}/invitations/${token}/accept`, { password, accept_terms: true }).then((r) => r.data),
  // admin
  admin: {
    dashboard: () => client.get("/admin/dashboard").then((r) => r.data),
    activity: (limit = 50) => client.get(`/admin/activity?limit=${limit}`).then((r) => r.data),
    listAdjusters: () => client.get("/admin/adjusters").then((r) => r.data),
    verify: (id, status, admin_note) => client.patch(`/admin/adjusters/${id}/verify`, { status, admin_note }).then((r) => r.data),
    suspend: (id) => client.patch(`/admin/adjusters/${id}/suspend`).then((r) => r.data),
    edit: (id, p) => client.patch(`/admin/adjusters/${id}`, p).then((r) => r.data),
    remove: (id) => client.delete(`/admin/adjusters/${id}`).then((r) => r.data),
    resetPassword: (id) => client.post(`/admin/adjusters/${id}/reset-password`).then((r) => r.data),
    // pending changes review
    listPendingChanges: () => client.get("/admin/pending-changes").then((r) => r.data),
    approveChanges: (id) => client.post(`/admin/adjusters/${id}/approve-changes`).then((r) => r.data),
    rejectChanges: (id) => client.post(`/admin/adjusters/${id}/reject-changes`).then((r) => r.data),
    // invitations
    listInvitations: () => client.get("/admin/invitations").then((r) => r.data),
    createInvitation: (email, first_name, last_name, role = "adjuster") =>
      client.post("/admin/invitations", { email, first_name, last_name, role }).then((r) => r.data),
    regenerateInvitation: (id) => client.post(`/admin/invitations/${id}/regenerate`).then((r) => r.data),
    resendInvitationEmail: (id) => client.post(`/admin/invitations/${id}/resend-email`).then((r) => r.data),
    revokeInvitation: (id) => client.delete(`/admin/invitations/${id}`).then((r) => r.data),
    emailStatus: () => client.get("/admin/email/status").then((r) => r.data),
    // team
    listTeam: () => client.get("/admin/team").then((r) => r.data),
    changeRole: (id, role) => client.patch(`/admin/team/${id}/role`, { role }).then((r) => r.data),
  },
};

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}
